import { useCallback, useEffect, useRef, useState } from "react";

// Location is compulsory for trip verification, so the portal has to know exactly *why* it is not
// available - the wording and the button that can actually fix it differ per state. A web page
// cannot switch on the device location service, and it cannot re-prompt once the driver has
// blocked the site, so those two cases must be told apart and explained rather than retried.
//
// States: checking | granted | prompt | denied | unavailable | unsupported | insecure
const PROBE_OPTIONS = { enableHighAccuracy: true, maximumAge: 30000, timeout: 12000 };

export default function useLocationGate() {
  const [state, setState] = useState("checking");
  const [accuracy, setAccuracy] = useState(null);
  const [busy, setBusy] = useState(false);
  const mountedRef = useRef(true);
  const permissionRef = useRef(null);

  useEffect(() => () => {
    mountedRef.current = false;
    if (permissionRef.current) permissionRef.current.onchange = null;
  }, []);

  const apply = useCallback((next, nextAccuracy) => {
    if (!mountedRef.current) return next;
    setState(next);
    if (nextAccuracy != null) setAccuracy(nextAccuracy);
    return next;
  }, []);

  const unsupportedState = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return "unsupported";
    // Chrome silently refuses geolocation outside a secure context, which otherwise looks
    // identical to a hardware failure to the driver.
    if (typeof window !== "undefined" && window.isSecureContext === false) return "insecure";
    return "";
  }, []);

  // Asks the device for a fix. Triggers the browser permission dialog when the driver has not
  // answered it yet, so it must only run from a user gesture or when permission is already granted.
  const probe = useCallback(() => {
    const blocked = unsupportedState();
    if (blocked) return Promise.resolve(apply(blocked));
    if (mountedRef.current) setBusy(true);
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (mountedRef.current) setBusy(false);
          resolve(apply("granted", position.coords.accuracy));
        },
        (error) => {
          if (mountedRef.current) setBusy(false);
          // 1 PERMISSION_DENIED, 2 POSITION_UNAVAILABLE, 3 TIMEOUT. Only the first one is the
          // driver blocking us; the rest mean the permission is fine but there is no fix yet.
          resolve(apply(error?.code === 1 ? "denied" : "unavailable"));
        },
        PROBE_OPTIONS,
      );
    });
  }, [apply, unsupportedState]);

  // Reads the current state without prompting, so the portal can show its own explanation first
  // instead of firing the browser dialog the moment the page opens.
  const refresh = useCallback(async () => {
    const blocked = unsupportedState();
    if (blocked) return apply(blocked);
    if (!navigator.permissions?.query) {
      // Older Safari has no Permissions API: we cannot tell granted from prompt without asking.
      return apply(state === "granted" ? "granted" : "prompt");
    }
    try {
      const result = await navigator.permissions.query({ name: "geolocation" });
      if (permissionRef.current !== result) {
        if (permissionRef.current) permissionRef.current.onchange = null;
        permissionRef.current = result;
        result.onchange = () => { refresh(); };
      }
      if (result.state === "granted") return probe();
      return apply(result.state === "denied" ? "denied" : "prompt");
    } catch {
      return apply("prompt");
    }
  }, [apply, probe, state, unsupportedState]);

  useEffect(() => {
    refresh();
    // Drivers fix this by leaving for Android settings and coming back, so re-test on return
    // rather than making them find a retry button.
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { state, accuracy, busy, ready: state === "granted", request: probe, refresh };
}
